import { temPermissao, useSessao } from '../../auth/contexto'
import { BotaoLink } from '../../components/Botao'
import { AvisoDeErro } from '../../components/Estados'
import { IconeNovo } from '../../components/Icones'
import { TEXTOS } from '../../lib/glossario'
import type { UsuarioLogado } from '../../types/gmud'
import { CartaoDeContagem } from './CartaoDeContagem'
import { CartaoDoHistorico } from './CartaoDoHistorico'
import { contagemPorExtenso, useContagensDoPainel, type Contagem } from './contagens'
import { EsperandoAlguem } from './EsperandoAlguem'
import { EsperandoVoce } from './EsperandoVoce'
import { PrimeirosPassos } from './PrimeirosPassos'

const ID_DO_RESUMO = 'painel-resumo-do-processo'

/**
 * "Olá, Ana", e não "Olá, Ana Carolina de Souza Ferreira".
 *
 * O nome completo vem do cadastro institucional e chega com quatro ou cinco partes; numa saudação
 * ele deixa de cumprimentar e passa a identificar, que é outra coisa. Quem não tem nome no `/me`
 * é cumprimentado do mesmo jeito — melhor sem nome do que com um espaço em branco no meio da frase.
 *
 * @param usuario o usuário logado, quando o `GET /me` já respondeu
 * @returns a saudação
 */
function saudacao(usuario: UsuarioLogado | null): string {
  const primeiroNome = usuario?.nome.trim().split(' ')[0]
  return primeiroNome ? `Olá, ${primeiroNome}` : 'Olá'
}

/**
 * A linha que diz de quantas solicitações estes números falam — e de quais não falam.
 *
 * A segunda frase aparece mesmo quando a contagem falha, porque ela não depende do número: é a
 * ressalva que impede alguém de ler os cartões como o retrato de tudo o que existe no banco.
 *
 * @param processo a contagem de tudo o que nasceu neste sistema
 * @returns a frase de contexto da grade
 */
function resumoDoProcesso(processo: Contagem): string {
  const ressalva = 'O histórico do sistema anterior não entra nestas contas.'
  if (processo.quantidade === null) {
    return ressalva
  }
  const palavra = processo.quantidade === 1 ? 'solicitação aberta' : 'solicitações abertas'
  return `${contagemPorExtenso(processo.quantidade)} ${palavra} neste sistema. ${ressalva}`
}

/**
 * A primeira tela de quem entra no sistema.
 *
 * Todo número daqui é um `GET /gmuds` com um recorte declarado em `recortes.ts` — não há endpoint
 * de métricas, e não há contagem calculada na tela a partir de uma lista baixada pela metade. Cada
 * cartão é um link para a lista com o MESMO recorte, então clicar num cartão que diz 4 abre uma
 * lista que diz 4.
 *
 * As contagens do processo excluem as 1.027 solicitações importadas do sistema anterior. Elas têm
 * bloco próprio no fim da página, separado de propósito: nenhuma passou por comitê, e somá-las
 * aqui faria a tela afirmar uma conformidade que não houve.
 *
 * @returns o painel
 */
export function PaginaPainel() {
  const { usuario } = useSessao()
  const { cartoes, processo, historico, erro } = useContagensDoPainel()
  const podeCriar = temPermissao(usuario, 'GMUD_CRIAR')
  const organizacao = usuario?.organizacao

  // Só depois de a contagem voltar: enquanto ela é nula, a tela não sabe se está vazia, e trocar
  // a grade pela orientação de primeiro acesso a cada recarregamento seria pior do que esperar.
  const semProcesso = processo.quantidade === 0

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="titulo-da-pagina">{saudacao(usuario)}</h1>
          <p className="mt-1 text-sm text-slate-600">
            {organizacao && (
              <>
                <span className="font-medium text-slate-800">{organizacao.sigla}</span>
                <span className="text-slate-400"> · </span>
              </>
            )}
            Este é o painel das solicitações de mudança.
          </p>
        </div>
        {podeCriar && (
          <BotaoLink to="/gmuds/novo" aparencia="primario">
            <IconeNovo className="h-4 w-4" />
            {TEXTOS.abrirSolicitacao}
          </BotaoLink>
        )}
      </header>

      {erro != null && <AvisoDeErro erro={erro} titulo="Não foi possível carregar os números do painel" />}

      {semProcesso ? (
        <PrimeirosPassos podeCriar={podeCriar} />
      ) : (
        <>
          <section aria-labelledby={ID_DO_RESUMO} className="space-y-3">
            <div>
              <h2 id={ID_DO_RESUMO} className="text-base font-semibold text-slate-900">
                Como estão as solicitações
              </h2>
              <p className="mt-0.5 max-w-prose text-sm text-slate-600">{resumoDoProcesso(processo)}</p>
            </div>
            <ul className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
              {cartoes.map(({ recorte, contagem }) => (
                <CartaoDeContagem key={recorte.id} recorte={recorte} contagem={contagem} />
              ))}
            </ul>
          </section>

          {/* Os dois convivem, e a ordem é a resposta a duas perguntas diferentes: "o que é meu?"
              (por papel, some quando vazio) vem antes de "o que está parado?" (por estado, o
              panorama). Trocar um pelo outro perderia informação — quem administra o contrato
              precisa enxergar o que trava com OUTRA pessoa, e não só o que trava consigo. */}
          <EsperandoVoce />
          <EsperandoAlguem />
        </>
      )}

      <CartaoDoHistorico contagem={historico} />
    </div>
  )
}
